/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useEffect } from "react";
import { apple, ORANGE, GREEN, BLUE, RED, YELLOW, PURPLE, Card, KpiTile, Badge, PageHeader, Btn, ChipRow, Table, StatusPill, Sparkline, MiniBars, Donut, CountUp, Section, TabBar, Icons } from "./shared";

/* ============================================================
   1. DISPATCH BOARD — live operations
   ============================================================ */
export function Dispatch() {
  return (
    <div>
      <PageHeader kicker="LIVE OPERATIONS" title="Dispatch Board" subtitle="Real-time fleet visibility · 32 in transit · 14 to assign · 3 exceptions"
        actions={<><Btn>Filters</Btn><Btn primary testid="dispatch-assign">Auto-Assign</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="In Transit" value={32} delta="↗ +4" icon={Icons.truck} />
        <KpiTile label="To Assign" value={14} delta="↗ +2" deltaColor={YELLOW} icon={Icons.alert} />
        <KpiTile label="At Risk" value={3} delta="2 weather, 1 HOS" deltaColor={RED} icon={Icons.bell} />
        <KpiTile label="Available" value="89 drivers" delta="22 within 100 mi" icon={Icons.person} />
        <KpiTile hero label="Today Revenue" value="$84,520" delta="↗ +15% pace" icon={Icons.dollar} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        <Card title="Fleet Live Map" right={<Btn size="sm">Layer ▾</Btn>}>
          <BigMap />
        </Card>
        <Card title="Exception Queue" right={<Btn ghost size="sm">View All</Btn>}>
          {[
            { title: "RY-78422 weather hold · Memphis", sub: "ETA slip +90m · Notify customer?", sev: RED },
            { title: "Driver D. Johnson · HOS in 38min", sub: "Auto-route to Yard #3?", sev: YELLOW },
            { title: "RY-78211 detention · LAX dock 4", sub: "$87 accruing · Bill customer?", sev: ORANGE },
          ].map((x, i) => (
            <div key={i} className="ry-row" style={{ padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", animationDelay: `${i * 80}ms` }}>
              <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: x.sev, marginTop: 6, animation: "ry-pulse-orange 1.6s infinite", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "white" }}>{x.title}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{x.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Active Shipments" right={<><Btn ghost size="sm">Filters</Btn><Btn ghost size="sm">Export</Btn></>}>
          <Table
            columns={[
              { key: "id", label: "ID", bold: true },
              { key: "lane", label: "Lane" },
              { key: "driver", label: "Driver" },
              { key: "stage", label: "Stage", cell: (r) => <StageBar stage={r.stageIdx} /> },
              { key: "eta", label: "ETA" },
              { key: "act", label: "", align: "right", cell: () => <Btn size="sm">Open</Btn> },
            ]}
            rows={[
              { id: "RY-78432", lane: "YYZ → ORD", driver: "D. Johnson", stageIdx: 4, eta: "Today 6:00 PM" },
              { id: "RY-78421", lane: "YUL → YYZ", driver: "D. Johnson", stageIdx: 5, eta: "Today 9:15 PM" },
              { id: "RY-78445", lane: "YYC → DFW", driver: "D. Johnson", stageIdx: 3, eta: "Tomorrow 4:30 PM" },
              { id: "RY-78451", lane: "YVR → JFK", driver: "Unassigned", stageIdx: 1, eta: "Pending" },
              { id: "RY-78422", lane: "MEM → BNA", driver: "D. Johnson", stageIdx: 4, eta: "+90m delay", flag: true },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}

function StageBar({ stage }) {
  const stages = ["Draft", "Quoted", "Booked", "Pickup", "Transit", "Delivered"];
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center", fontSize: 9.5, color: "rgba(255,255,255,0.7)", minWidth: 220 }}>
      {stages.map((s, i) => (
        <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= stage ? ORANGE : "rgba(255,255,255,0.1)", boxShadow: i === stage ? `0 0 8px ${ORANGE}88` : "none" }} title={s} />
      ))}
      <span style={{ marginLeft: 8, color: ORANGE, fontWeight: 700, fontSize: 10, minWidth: 60 }}>{stages[stage]}</span>
    </div>
  );
}

function BigMap() {
  return (
    <div style={{ position: "relative", height: 380, borderRadius: 10, overflow: "hidden", background: "linear-gradient(135deg, #16161a, #0c0c10)", border: "0.5px solid rgba(255,255,255,0.05)" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.22, backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`, backgroundSize: "30px 30px" }} />
      {/* Coast lines */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 600 380" preserveAspectRatio="none">
        {/* USA outline-ish */}
        <path d="M40,140 Q120,80 220,90 T420,80 Q500,90 560,150 L560,260 Q480,310 380,300 T180,310 Q90,290 40,240 Z" stroke="rgba(255,255,255,0.10)" strokeWidth="1.2" fill="rgba(255,255,255,0.012)" />
        {/* Lanes */}
        <path d="M120,180 Q220,120 320,170 T520,150" stroke={ORANGE} strokeWidth="1.4" strokeDasharray="4 5" fill="none" />
        <path d="M80,250 Q200,290 360,260 T540,240" stroke={GREEN} strokeWidth="1.2" strokeDasharray="4 5" fill="none" />
        <path d="M140,110 Q260,160 380,220 T500,290" stroke={BLUE} strokeWidth="1.2" strokeDasharray="4 5" fill="none" />
      </svg>
      {[
        { x: 18, y: 48, c: ORANGE, l: "T-204 · YYZ" },
        { x: 40, y: 50, c: ORANGE, l: "" },
        { x: 56, y: 44, c: ORANGE, l: "ORD" },
        { x: 76, y: 38, c: GREEN, l: "T-118 · NYC" },
        { x: 24, y: 70, c: GREEN, l: "T-307 · LAX" },
        { x: 60, y: 73, c: BLUE, l: "DFW" },
        { x: 86, y: 64, c: ORANGE, l: "T-602 · MIA" },
        { x: 38, y: 28, c: YELLOW, l: "T-411 · YVR" },
        { x: 70, y: 56, c: RED, l: "RY-78422 ⚠" },
      ].map((p, i) => (
        <div key={i} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}>
          <span style={{ position: "absolute", top: "50%", left: "50%", width: 22, height: 22, borderRadius: "50%", background: p.c, opacity: 0.35, animation: "ry-pulse-ring 1.8s ease-out infinite", animationDelay: `${i * 200}ms`, transform: "translate(-50%, -50%)" }} />
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: p.c, border: "1.5px solid #0a0a0a", boxShadow: `0 0 12px ${p.c}66`, position: "relative", zIndex: 2 }} />
          {p.l && <div style={{ position: "absolute", top: "-22px", left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontSize: 9.5, fontWeight: 700, color: "white", background: "rgba(0,0,0,0.7)", padding: "2px 6px", borderRadius: 4, border: `0.5px solid ${p.c}88` }}>{p.l}</div>}
        </div>
      ))}
      {/* Legend */}
      <div style={{ position: "absolute", left: 10, bottom: 10, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 9, padding: 10, fontSize: 10.5, color: "white", display: "flex", flexDirection: "column", gap: 5 }}>
        <Legend dot={ORANGE} label="In Transit · 32" />
        <Legend dot={GREEN} label="Available · 89" />
        <Legend dot={YELLOW} label="HOS Warn · 4" />
        <Legend dot={RED} label="Exception · 3" />
      </div>
      <div style={{ position: "absolute", right: 10, top: 10, display: "flex", flexDirection: "column", gap: 4 }}>
        {["+", "−"].map((s) => <button key={s} style={{ width: 26, height: 26, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", border: "0.5px solid rgba(255,255,255,0.08)", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", borderRadius: 6 }}>{s}</button>)}
      </div>
    </div>
  );
}
function Legend({ dot, label }) { return <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />{label}</span>; }

/* ============================================================
   2. SHIPMENT DETAIL — pipeline, stops, margin, docs, audit
   ============================================================ */
export function ShipmentDetail() {
  const [tab, setTab] = useState("Overview");
  return (
    <div>
      <PageHeader kicker="SHIPMENT · RY-78432" title="Toronto, ON → Chicago, IL"
        subtitle="ABC Manufacturing · 53′ Dry Van · 38,200 lbs · D. Johnson · ETA Today 6:00 PM"
        actions={<><Btn>Print BOL</Btn><Btn>Message Driver</Btn><Btn primary>Update Status</Btn></>} />

      {/* Status pipeline */}
      <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 18, marginBottom: 14 }}>
        <PipelineBar />
      </div>

      <TabBar tabs={["Overview", "Stops & Map", "Margin", "Documents", "Audit Log", "Communications"]} active={tab} onChange={setTab} />

      {tab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card title="Trip Summary">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                {[
                  { l: "Total Miles", v: "813" },
                  { l: "Drive Time", v: "12h 40m" },
                  { l: "Crossings", v: "1 (Detroit)" },
                  { l: "Fuel Est.", v: "$412" },
                  { l: "Detention Risk", v: "Low", c: GREEN },
                  { l: "Weather Risk", v: "Light snow MI", c: YELLOW },
                  { l: "Customer Tier", v: "A · Net 30" },
                  { l: "Lane History", v: "188 prior loads" },
                ].map((s) => (
                  <div key={s.l}>
                    <div style={{ fontSize: 10, letterSpacing: 0.5, color: "rgba(255,255,255,0.5)", fontWeight: 700, textTransform: "uppercase" }}>{s.l}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: s.c || "white", marginTop: 3 }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Live Tracking" right={<Badge color={ORANGE}>● TRANSIT</Badge>}>
              <BigMap />
              <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "rgba(255,255,255,0.75)" }}>
                <span><strong style={{ color: "white" }}>Current:</strong> I-94 W · 32mi from Chicago</span>
                <span><strong style={{ color: "white" }}>Speed:</strong> 62 mph</span>
                <span><strong style={{ color: ORANGE }}>ETA:</strong> 6:04 PM CT</span>
              </div>
            </Card>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card title="Margin">
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}><Donut pct={28} color={GREEN} size={120} thickness={10} label="+28%" /></div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
                <Row k="Revenue" v="$4,250" />
                <Row k="Driver pay" v="−$2,640" />
                <Row k="Fuel" v="−$412" />
                <Row k="Crossing/Toll" v="−$135" />
                <Row k="Insurance" v="−$72" />
                <hr style={{ border: "none", borderTop: "0.5px solid rgba(255,255,255,0.1)", margin: "8px 0" }} />
                <Row k="Net" v="+$991" bold />
              </div>
            </Card>
            <Card title="Quick Actions">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {["Reassign Driver", "Add Stop", "Issue BOL", "Hold Shipment", "Create Invoice", "Open Incident"].map((a) => (
                  <Btn key={a} size="sm" style={{ width: "100%", textAlign: "left" }}>{a}</Btn>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "Stops & Map" && <StopsTimeline />}
      {tab === "Margin" && <MarginBreakdown />}
      {tab === "Documents" && <DocumentsList />}
      {tab === "Audit Log" && <ShipmentAudit />}
      {tab === "Communications" && <ShipmentComms />}
    </div>
  );
}
function Row({ k, v, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
      <span style={{ color: "rgba(255,255,255,0.7)" }}>{k}</span>
      <span style={{ color: "white", fontWeight: bold ? 800 : 700, fontSize: bold ? 13 : 12 }}>{v}</span>
    </div>
  );
}

function PipelineBar() {
  const stages = [
    { k: "Draft", t: "May 17 9:12 AM" },
    { k: "Quoted", t: "May 17 9:14 AM" },
    { k: "Booked", t: "May 17 10:02 AM" },
    { k: "Pickup", t: "May 19 9:15 AM" },
    { k: "In Transit", t: "May 19 11:40 AM", active: true },
    { k: "Delivered", t: "Today 6:00 PM" },
    { k: "Invoiced", t: "—" },
    { k: "Completed", t: "—" },
  ];
  const activeIdx = stages.findIndex((s) => s.active);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {stages.map((s, i) => {
        const past = i < activeIdx;
        const now = i === activeIdx;
        const future = i > activeIdx;
        return (
          <React.Fragment key={s.k}>
            <div style={{ flex: "0 0 auto", textAlign: "center", minWidth: 70 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: past ? GREEN : now ? ORANGE : "rgba(255,255,255,0.06)", border: `1px solid ${past ? GREEN : now ? ORANGE : "rgba(255,255,255,0.12)"}`, color: "white", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, boxShadow: now ? `0 0 18px ${ORANGE}88` : "none", animation: now ? "ry-pulse-orange 1.6s infinite" : "none" }}>
                {past ? "✓" : i + 1}
              </div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: now ? ORANGE : past ? "white" : "rgba(255,255,255,0.45)", marginTop: 6 }}>{s.k}</div>
              <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{s.t}</div>
            </div>
            {i < stages.length - 1 && <div style={{ flex: 1, height: 2, background: i < activeIdx ? GREEN : "rgba(255,255,255,0.06)", margin: "0 -3px", marginBottom: 32 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StopsTimeline() {
  const stops = [
    { k: "PICKUP", title: "ABC Mfg. — 12 Industrial Pkwy", city: "Toronto, ON", t: "May 19 · 9:15 AM", status: "Completed", c: GREEN, sub: "Loaded 28 pallets · Sealed #84291" },
    { k: "BORDER", title: "Detroit-Windsor Tunnel", city: "ACE eManifest", t: "May 19 · 6:42 PM", status: "Cleared", c: GREEN, sub: "Customs fee $135" },
    { k: "FUEL", title: "TA Travel Center", city: "Toledo, OH", t: "May 19 · 11:20 PM", status: "Stop", c: BLUE, sub: "120 gal · $412 · RY card •••• 4814" },
    { k: "DROP", title: "ABC Mfg. — 451 Cargo Rd", city: "Chicago, IL", t: "Today · 6:00 PM (ETA)", status: "Pending", c: ORANGE, sub: "POD signature required" },
  ];
  return (
    <Card title="Stops & Route Events">
      <div style={{ position: "relative", paddingLeft: 8 }}>
        <div style={{ position: "absolute", left: 18, top: 14, bottom: 14, width: 2, background: "rgba(255,255,255,0.08)" }} />
        {stops.map((s, i) => (
          <div key={i} className="ry-row" style={{ display: "flex", gap: 14, padding: "12px 0", animationDelay: `${i * 80}ms` }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: s.c, border: "2.5px solid #131318", boxShadow: `0 0 10px ${s.c}66`, position: "relative", zIndex: 1 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div>
                  <div style={{ fontSize: 9.5, letterSpacing: 1, color: s.c, fontWeight: 800 }}>{s.k}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "white", marginTop: 2 }}>{s.title}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.6)" }}>{s.city}</div>
                </div>
                <Badge color={s.c}>{s.status}</Badge>
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{s.t} · {s.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MarginBreakdown() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
      <Card title="Cost Structure">
        <Table compact columns={[
          { key: "k", label: "Category", bold: true },
          { key: "v", label: "Amount", align: "right" },
          { key: "p", label: "% of Rev", align: "right" },
        ]} rows={[
          { k: "Line Haul Revenue", v: "+$3,900", p: "91.8%" },
          { k: "Fuel Surcharge", v: "+$280", p: "6.6%" },
          { k: "Customs / ACE", v: "+$70", p: "1.6%" },
          { k: "── Total Revenue", v: "$4,250", p: "100.0%" },
          { k: "Driver Pay", v: "−$2,640", p: "62.1%" },
          { k: "Fuel @$3.91/gal", v: "−$412", p: "9.7%" },
          { k: "Tolls/Crossings", v: "−$135", p: "3.2%" },
          { k: "Cargo Insurance", v: "−$72", p: "1.7%" },
          { k: "── Total Costs", v: "−$3,259", p: "76.7%" },
          { k: "Net Margin", v: "+$991", p: "23.3%" },
        ]} />
      </Card>
      <Card title="Margin Comparison">
        <div style={{ padding: "10px 0", textAlign: "center" }}>
          <Donut pct={23} color={GREEN} size={150} thickness={14} label="23.3%" />
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 8 }}>Healthy · above 18% floor</div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Row k="Lane avg margin" v="22.1%" />
          <Row k="Customer avg margin" v="26.2%" />
          <Row k="Org target" v="≥ 20%" />
        </div>
      </Card>
    </div>
  );
}

function DocumentsList() {
  return (
    <Table columns={[
      { key: "type", label: "Type", bold: true },
      { key: "name", label: "File" },
      { key: "uploaded", label: "Uploaded by" },
      { key: "when", label: "When" },
      { key: "status", label: "Status", cell: (r) => <Badge color={r.c}>{r.status}</Badge> },
      { key: "act", label: "", align: "right", cell: () => <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}><Btn size="sm">{Icons.eye}</Btn><Btn size="sm">{Icons.download}</Btn></span> },
    ]} rows={[
      { type: "BOL", name: "BOL-RY-78432.pdf", uploaded: "D. Johnson", when: "May 19 · 9:18 AM", status: "Signed", c: GREEN },
      { type: "Rate Conf", name: "RC-RY-78432.pdf", uploaded: "Dwayne Johnson", when: "May 17 · 10:04 AM", status: "Signed", c: GREEN },
      { type: "ACE Manifest", name: "ACE-39204-1825.pdf", uploaded: "Auto", when: "May 19 · 5:30 PM", status: "Filed", c: GREEN },
      { type: "Insurance COI", name: "COI-ABC-2025.pdf", uploaded: "ABC Mfg.", when: "Jan 4 · 11:00 AM", status: "On file", c: GREEN },
      { type: "Pickup Photos", name: "pickup_4_imgs.zip", uploaded: "D. Johnson", when: "May 19 · 9:22 AM", status: "Attached", c: BLUE },
      { type: "POD", name: "—", uploaded: "—", when: "—", status: "Pending", c: ORANGE },
    ]} />
  );
}

function ShipmentAudit() {
  const events = [
    { t: "Now", who: "System", act: "Geo fence entered Chicago city limit", c: BLUE },
    { t: "12 min ago", who: "D. Johnson", act: "Speed limit alert: 67 mph zone", c: YELLOW },
    { t: "2h ago", who: "D. Johnson", act: "Fuel stop: TA Toledo · 120 gal · $412", c: BLUE },
    { t: "May 19 · 6:42 PM", who: "ACE", act: "Border crossing cleared · Detroit-Windsor", c: GREEN },
    { t: "May 19 · 9:18 AM", who: "D. Johnson", act: "BOL signed and uploaded", c: GREEN },
    { t: "May 19 · 9:15 AM", who: "D. Johnson", act: "Pickup completed · seal #84291", c: GREEN },
    { t: "May 17 · 10:04 AM", who: "Dwayne Johnson", act: "Driver assigned · D. Johnson · T-204", c: ORANGE },
    { t: "May 17 · 10:02 AM", who: "Dwayne Johnson", act: "Shipment booked at $4,250", c: ORANGE },
    { t: "May 17 · 9:14 AM", who: "AI Pricing", act: "Quote suggested $2,180 – $2,450", c: PURPLE },
    { t: "May 17 · 9:12 AM", who: "Dwayne Johnson (ABC)", act: "Shipment requested from customer portal", c: BLUE },
  ];
  return (
    <Card title="Audit Trail" right={<Badge color={ORANGE}>Append-only</Badge>}>
      {events.map((e, i) => (
        <div key={i} className="ry-row" style={{ display: "flex", gap: 12, padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", animationDelay: `${i * 40}ms` }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: e.c, marginTop: 6, flexShrink: 0, boxShadow: `0 0 6px ${e.c}88` }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, color: "white" }}>{e.act}</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{e.who} · {e.t}</div>
          </div>
        </div>
      ))}
    </Card>
  );
}

function ShipmentComms() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      <Card title="With Driver (SMS)">
        <ChatThread who="Driver" name="D. Johnson" msgs={[
          { from: "you", t: "9:14 AM", body: "Load assigned. Pick up at 9:00, dock 4." },
          { from: "them", t: "9:15 AM", body: "10-4. Heading there now." },
          { from: "them", t: "9:18 AM", body: "BOL signed, photos uploaded." },
          { from: "you", t: "5:32 PM", body: "Customer asking about ETA." },
          { from: "them", t: "5:36 PM", body: "32 mi out, on track for 6:00 PM." },
        ]} />
      </Card>
      <Card title="With Customer (Email)">
        <ChatThread who="Customer" name="Dwayne Johnson · ABC Mfg." msgs={[
          { from: "them", t: "May 17 9:08 AM", body: "Can you book this Toronto → Chicago, 28 pallets, May 19 pickup?" },
          { from: "you", t: "May 17 9:14 AM", body: "Yes. AI quote: $4,250 all-in. Booking now." },
          { from: "them", t: "May 17 10:30 AM", body: "Approved. POD email to receiving@abcmfg.com." },
          { from: "you", t: "Today 5:32 PM", body: "On track for 6:00 PM. POD will be auto-sent on signature." },
        ]} />
      </Card>
    </div>
  );
}
function ChatThread({ who, name, msgs }) {
  return (
    <>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 8 }}>{who} · <strong style={{ color: "white" }}>{name}</strong></div>
      <div style={{ background: "rgba(0,0,0,0.25)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 10, maxHeight: 280, overflow: "auto" }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.from === "you" ? "flex-end" : "flex-start", marginBottom: 6 }}>
            <div style={{ maxWidth: "78%", background: m.from === "you" ? ORANGE : "rgba(255,255,255,0.08)", color: "white", padding: "6px 10px", borderRadius: 9, fontSize: 11.5, lineHeight: 1.4 }}>
              {m.body}
              <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 2, textAlign: m.from === "you" ? "right" : "left" }}>{m.t}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 9, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "rgba(255,255,255,0.5)" }}>
        Reply… <span style={{ marginLeft: "auto", color: ORANGE }}>{Icons.send}</span>
      </div>
    </>
  );
}

/* ============================================================
   3. AI QUOTE ENGINE
   ============================================================ */
export function AIQuote() {
  return (
    <div>
      <PageHeader kicker="PRICING" title="AI Quote & Pricing Engine" subtitle="248 lanes trained · re-trained 12h ago · 97.4% accuracy vs win rate"
        actions={<><Btn>Training Data</Btn><Btn primary testid="ai-quote-new">+ New Quote</Btn></>} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Quotes Today" value={42} delta="↗ +12%" icon={Icons.bolt} />
        <KpiTile label="Win Rate" value="38.4%" delta="↗ +2.1pt" icon={Icons.check} />
        <KpiTile label="Avg Margin Quoted" value="24.6%" delta="↗ +0.4pt" icon={Icons.margin} />
        <KpiTile hero label="MTD Quoted Revenue" value="$1.84M" delta="↗ +18%" icon={Icons.dollar} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Card title="Quote Builder">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[["Origin", "Toronto, ON"], ["Destination", "Chicago, IL"], ["Equipment", "53′ Dry Van"], ["Weight", "38,200 lbs"], ["Pickup", "May 20, 8:00 AM"], ["Service", "Standard 1-day"]].map(([l, v]) => (
              <div key={l} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 9, padding: "10px 12px" }}>
                <div style={{ fontSize: 9.5, letterSpacing: 0.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase" }}>{l}</div>
                <div style={{ fontSize: 13, color: "white", marginTop: 3, fontWeight: 600 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, background: "linear-gradient(135deg, rgba(255,107,0,0.10), rgba(168,85,247,0.10))", border: `0.5px solid ${ORANGE}66`, borderRadius: 11, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ color: ORANGE, animation: "ry-ai-spin 3s linear infinite" }}>{Icons.cpu}</div>
              <div style={{ fontSize: 11, letterSpacing: 1.4, color: ORANGE, fontWeight: 700 }}>AI ANALYSIS</div>
              <span style={{ marginLeft: "auto", fontSize: 10, color: "rgba(255,255,255,0.5)" }}>248 lanes · 4 carriers · live fuel</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
              {[{ l: "Floor (min)", v: "$2,080", c: RED }, { l: "Suggested", v: "$2,300", c: ORANGE, hl: true }, { l: "Ceiling (max)", v: "$2,520", c: GREEN }].map((b) => (
                <div key={b.l} style={{ background: b.hl ? `${b.c}22` : "rgba(255,255,255,0.04)", border: b.hl ? `1px solid ${b.c}` : `0.5px solid ${b.c}44`, borderRadius: 9, padding: "10px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.6)", letterSpacing: 0.5, textTransform: "uppercase" }}>{b.l}</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: b.hl ? b.c : "white", marginTop: 4 }}>{b.v}</div>
                </div>
              ))}
            </div>
          </div>
          <Btn primary style={{ width: "100%", marginTop: 14, padding: "10px 14px" }}>Send Quote $2,300 to ABC Manufacturing →</Btn>
        </Card>

        <Card title="Lane Intelligence · YYZ → ORD">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 4 }}>RATE HISTORY · LAST 30 DAYS</div>
            <Sparkline points={[2100, 2150, 2180, 2120, 2200, 2240, 2180, 2260, 2300, 2280, 2320, 2380, 2300]} width={420} height={70} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
            {[
              { l: "188 prior loads", c: "white" },
              { l: "Avg rate $2,265", c: "white" },
              { l: "Capacity tight (8% above)", c: YELLOW },
              { l: "Fuel index $3.91/gal", c: "white" },
              { l: "Detention risk: low", c: GREEN },
              { l: "Avg transit 12.4h", c: "white" },
            ].map((r) => (
              <div key={r.l} style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px", fontSize: 11.5, color: r.c, fontWeight: 600 }}>• {r.l}</div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 6 }}>WIN PROBABILITY @ PRICE</div>
            <WinCurve />
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Recent Quotes">
          <Table columns={[
            { key: "id", label: "Quote", bold: true },
            { key: "cust", label: "Customer" },
            { key: "lane", label: "Lane" },
            { key: "rate", label: "Rate", align: "right" },
            { key: "margin", label: "Margin" },
            { key: "win", label: "Win Prob" },
            { key: "status", label: "Status", cell: (r) => <Badge color={r.c}>{r.status}</Badge> },
          ]} rows={[
            { id: "QTE-2052", cust: "Global Tech", lane: "ATL → DFW", rate: "$2,875", margin: "31.5%", win: "78%", status: "Sent", c: ORANGE },
            { id: "QTE-2051", cust: "ABC Mfg.", lane: "YYZ → ORD", rate: "$4,250", margin: "23.3%", win: "—", status: "Won", c: GREEN },
            { id: "QTE-2050", cust: "Sunrise Dist.", lane: "LA → PHX", rate: "$3,960", margin: "18.4%", win: "44%", status: "Lost", c: RED },
            { id: "QTE-2049", cust: "Retail Supply", lane: "MEM → BNA", rate: "$1,125", margin: "22.8%", win: "62%", status: "Pending", c: YELLOW },
          ]} />
        </Card>
      </div>
    </div>
  );
}
function WinCurve() {
  const pts = [{ p: 2080, w: 90 }, { p: 2200, w: 78 }, { p: 2300, w: 62 }, { p: 2400, w: 41 }, { p: 2520, w: 18 }];
  const W = 420, H = 80;
  const points = pts.map((d, i) => `${(i / (pts.length - 1)) * W},${H - (d.w / 100) * H}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 18}`}>
      <polyline points={points} stroke={ORANGE} strokeWidth="2" fill="none" />
      <polyline points={`0,${H} ${points} ${W},${H}`} fill={`${ORANGE}22`} stroke="none" />
      {pts.map((d, i) => (
        <g key={i}>
          <circle cx={(i / (pts.length - 1)) * W} cy={H - (d.w / 100) * H} r="3" fill={ORANGE} />
          <text x={(i / (pts.length - 1)) * W} y={H + 12} fontSize="9" fill="rgba(255,255,255,0.5)" textAnchor="middle">${d.p}</text>
          <text x={(i / (pts.length - 1)) * W} y={H - (d.w / 100) * H - 6} fontSize="9" fill="white" fontWeight="700" textAnchor="middle">{d.w}%</text>
        </g>
      ))}
    </svg>
  );
}

/* ============================================================
   4. BIDDING / LOAD MARKETPLACE
   ============================================================ */
export function Bidding() {
  const [selected, setSelected] = useState("LD-2014");
  return (
    <div>
      <PageHeader kicker="MARKETPLACE" title="Driver Bidding & Load Board" subtitle="18 open loads · 47 active bids · avg time-to-cover 12 min"
        actions={<><Btn>Bid Settings</Btn><Btn primary>+ Post Load</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Open Loads" value={18} icon={Icons.pkg} />
        <KpiTile label="Active Bids" value={47} delta="↗ avg 4 per load" icon={Icons.bolt} />
        <KpiTile label="Avg Cover Time" value="12 min" delta="↓ 4 min" deltaColor={GREEN} icon={Icons.history} />
        <KpiTile hero label="Avg Margin Gain" value="+4.2pt" delta="via reverse bidding" icon={Icons.margin} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 14 }}>
        <Card title="Open Loads">
          {[
            { id: "LD-2014", lane: "YYZ → ORD", w: "38.2K", rate: "$2,300", bids: 6 },
            { id: "LD-2013", lane: "YYC → DFW", w: "42.1K", rate: "$3,180", bids: 4 },
            { id: "LD-2012", lane: "YVR → SEA", w: "21.0K", rate: "$890", bids: 8 },
            { id: "LD-2011", lane: "MEM → BNA", w: "18.5K", rate: "$1,125", bids: 3 },
            { id: "LD-2010", lane: "LAX → PHX", w: "36.0K", rate: "$1,820", bids: 5 },
          ].map((l) => (
            <div key={l.id} onClick={() => setSelected(l.id)} className="ry-clickable" style={{ padding: "10px 8px", borderRadius: 8, border: `0.5px solid ${selected === l.id ? ORANGE : "transparent"}`, background: selected === l.id ? `${ORANGE}1a` : "transparent", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{l.id}</div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>{l.lane} · {l.w} lbs</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: ORANGE }}>{l.rate}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{l.bids} bids ●</div>
              </div>
            </div>
          ))}
        </Card>
        <Card title={`${selected} · YYZ → ORD · 38,200 lbs · Pickup May 20`}>
          <div style={{ background: `${ORANGE}10`, border: `0.5px solid ${ORANGE}44`, borderRadius: 10, padding: 10, marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
            <div><span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>POSTED RATE</span><div style={{ fontSize: 18, fontWeight: 800 }}>$2,300</div></div>
            <div><span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>BUDGET FLOOR</span><div style={{ fontSize: 18, fontWeight: 800, color: RED }}>$2,080</div></div>
            <div><span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>BEST BID</span><div style={{ fontSize: 18, fontWeight: 800, color: GREEN }}>$2,150</div></div>
            <div><span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>MARGIN @ BEST</span><div style={{ fontSize: 18, fontWeight: 800, color: GREEN }}>+31.4%</div></div>
          </div>
          <div style={{ fontSize: 10.5, letterSpacing: 1, color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 6 }}>BID COMPARISON · 6 BIDS</div>
          <Table compact columns={[
            { key: "driver", label: "Driver", bold: true },
            { key: "tier", label: "Tier", cell: (r) => <Badge color={r.tierC}>{r.tier}</Badge> },
            { key: "bid", label: "Bid", align: "right" },
            { key: "eta", label: "ETA" },
            { key: "miles", label: "DH mi", align: "right" },
            { key: "rating", label: "Rating" },
            { key: "margin", label: "Margin", align: "right", cell: (r) => <span style={{ color: r.margC, fontWeight: 800 }}>{r.margin}</span> },
            { key: "act", label: "", align: "right", cell: (r) => r.best ? <Btn primary size="sm">Accept</Btn> : <Btn size="sm">Counter</Btn> },
          ]} rows={[
            { driver: "D. Johnson", tier: "RY+ Gold", tierC: YELLOW, bid: "$2,150", eta: "May 20 6:00p", miles: 14, rating: "4.96 ★", margin: "+31.4%", margC: GREEN, best: true },
            { driver: "D. Johnson", tier: "RY+ Gold", tierC: YELLOW, bid: "$2,180", eta: "May 20 6:30p", miles: 22, rating: "4.92 ★", margin: "+30.0%", margC: GREEN },
            { driver: "D. Johnson", tier: "RY+ Silver", tierC: "rgba(255,255,255,0.7)", bid: "$2,210", eta: "May 20 8:00p", miles: 18, rating: "4.88 ★", margin: "+28.7%", margC: GREEN },
            { driver: "D. Johnson", tier: "Basic", tierC: "rgba(255,255,255,0.5)", bid: "$2,240", eta: "May 20 9:15p", miles: 35, rating: "4.84 ★", margin: "+27.3%", margC: GREEN },
            { driver: "D. Johnson", tier: "RY+ Gold", tierC: YELLOW, bid: "$2,280", eta: "May 20 7:00p", miles: 8, rating: "4.91 ★", margin: "+25.4%", margC: ORANGE },
            { driver: "D. Johnson", tier: "RY+ Silver", tierC: "rgba(255,255,255,0.7)", bid: "$2,300", eta: "May 20 11:00p", miles: 41, rating: "4.79 ★", margin: "+24.6%", margC: ORANGE },
          ]} />
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   5. DOCUMENT MANAGEMENT
   ============================================================ */
export function Documents() {
  return (
    <div>
      <PageHeader kicker="DOCS" title="Document Management" subtitle="4,218 documents · 18 awaiting signature · 12 expiring this month"
        actions={<><Btn>Templates</Btn><Btn primary>{Icons.upload} Upload</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { l: "BOL / POD", n: 1842, c: ORANGE },
          { l: "Rate Conf", n: 968, c: BLUE },
          { l: "COI (Insurance)", n: 312, c: GREEN },
          { l: "ACE/ACI", n: 412, c: PURPLE },
          { l: "W-9 / MC", n: 684, c: YELLOW },
        ].map((d) => (
          <div key={d.l} className="ry-cmd-tile" style={{ background: "rgba(255,255,255,0.025)", border: `0.5px solid ${d.c}33`, borderRadius: 11, padding: 14, cursor: "pointer" }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${d.c}22`, border: `0.5px solid ${d.c}55`, color: d.c, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>{Icons.doc}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5 }}><CountUp value={d.n} /></div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>{d.l}</div>
          </div>
        ))}
      </div>
      <ChipRow chips={["All · 4,218", "Awaiting Signature · 18", "Expiring · 12", "Drafts · 6"]} />
      <Table columns={[
        { key: "type", label: "Type", bold: true },
        { key: "name", label: "File" },
        { key: "linked", label: "Linked To" },
        { key: "owner", label: "Owner" },
        { key: "expires", label: "Expires" },
        { key: "status", label: "Status", cell: (r) => <Badge color={r.c}>{r.status}</Badge> },
        { key: "act", label: "", align: "right", cell: () => <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}><Btn size="sm">{Icons.eye}</Btn><Btn size="sm">{Icons.download}</Btn><Btn size="sm">{Icons.more}</Btn></span> },
      ]} rows={[
        { type: "BOL", name: "BOL-RY-78432.pdf", linked: "RY-78432", owner: "D. Johnson", expires: "—", status: "Signed", c: GREEN },
        { type: "Rate Conf", name: "RC-RY-78445.pdf", linked: "RY-78445 · YYC → DFW", owner: "Dwayne Johnson", expires: "—", status: "Awaiting Sig", c: ORANGE },
        { type: "COI", name: "COI-ABC-2025.pdf", linked: "ABC Manufacturing", owner: "ABC Mfg.", expires: "Jan 4, 2026", status: "Active", c: GREEN },
        { type: "MC Authority", name: "MC-948231.pdf", linked: "D. Johnson", owner: "Driver", expires: "Mar 2027", status: "Active", c: GREEN },
        { type: "Medical Card", name: "MED-J.Alvarez.pdf", linked: "D. Johnson", owner: "Driver", expires: "Jun 12, 2025", status: "Expiring", c: YELLOW },
        { type: "ACE Manifest", name: "ACE-39204.pdf", linked: "RY-78432", owner: "Auto", expires: "—", status: "Filed", c: GREEN },
        { type: "W-9", name: "W9-D.Sanchez.pdf", linked: "D. Johnson", owner: "Driver", expires: "—", status: "On file", c: GREEN },
        { type: "Insurance COI", name: "COI-Sunrise.pdf", linked: "Sunrise Dist.", owner: "Sunrise", expires: "May 31, 2025", status: "Renew", c: RED },
      ]} />
    </div>
  );
}

/* ============================================================
   6. CUSTOMER DETAIL (CRM)
   ============================================================ */
export function CustomerDetail() {
  const [tab, setTab] = useState("Overview");
  return (
    <div>
      <PageHeader kicker="CUSTOMER" title="ABC Manufacturing Inc."
        subtitle="A-tier · Net 30 · 188 loads YTD · $284K revenue · 26.2% avg margin"
        actions={<><Btn>Message</Btn><Btn>New Quote</Btn><Btn primary>New Shipment</Btn></>} />

      <TabBar tabs={["Overview", "Contacts", "Lanes", "Contracts", "Documents", "Financial", "Activity"]} active={tab} onChange={setTab} />

      {tab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <KpiTile label="YTD Revenue" value="$284K" delta="↗ +21%" icon={Icons.dollar} />
              <KpiTile label="Loads YTD" value={188} delta="↗ +14%" icon={Icons.pkg} />
              <KpiTile label="Avg Margin" value="26.2%" delta="↗ +2.1pt" icon={Icons.margin} />
              <KpiTile label="OTD" value="99.4%" delta="↗ +0.6pt" icon={Icons.check} />
            </div>
            <Card title="Revenue Last 12 Months">
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140, padding: "8px 0" }}>
                {[14, 18, 16, 22, 19, 24, 21, 26, 28, 27, 31, 32].map((v, i) => (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div className="ry-bar" style={{ width: "100%", height: `${(v / 32) * 100}%`, background: `linear-gradient(180deg, ${ORANGE}, ${ORANGE}44)`, borderRadius: "4px 4px 0 0", animationDelay: `${i * 50}ms` }} />
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)" }}>${v}K</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Preferred Lanes (heatmap)">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {[
                  { lane: "YYZ → ORD", count: 78, rev: "$118K" },
                  { lane: "YUL → JFK", count: 42, rev: "$64K" },
                  { lane: "YYZ → DTW", count: 28, rev: "$42K" },
                  { lane: "YYC → SEA", count: 18, rev: "$31K" },
                  { lane: "YVR → LAX", count: 14, rev: "$22K" },
                  { lane: "YHM → CLE", count: 8, rev: "$7K" },
                ].map((l, i) => (
                  <div key={l.lane} style={{ background: `linear-gradient(135deg, ${ORANGE}${Math.max(8, 30 - i * 4).toString(16)}, transparent)`, border: `0.5px solid ${ORANGE}55`, borderRadius: 9, padding: 10 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{l.lane}</div>
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{l.count} loads · {l.rev}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card title="Account Health">
              <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}><Donut pct={92} color={GREEN} size={130} thickness={12} label="92" /></div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textAlign: "center", marginBottom: 12 }}>Excellent · top 8% of accounts</div>
              <Row k="On-time pay" v="100%" />
              <Row k="Disputes" v="0" />
              <Row k="NPS" v="9 / 10" />
              <Row k="Last contact" v="May 17" />
            </Card>
            <Card title="Primary Contacts">
              {[{ n: "Dwayne Johnson", r: "Logistics Director", c: "jen@abcmfg.com" }, { n: "Dwayne Johnson", r: "Accounts Payable", c: "ap@abcmfg.com" }].map((c) => (
                <div key={c.n} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: c.n.startsWith("Kim") ? "0.5px solid rgba(255,255,255,0.06)" : "none" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${ORANGE}22`, border: `0.5px solid ${ORANGE}55`, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12 }}>{c.n.split(" ").map((x) => x[0]).join("")}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>{c.n}</div>
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)" }}>{c.r}</div>
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)", marginTop: 1 }}>{c.c}</div>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {tab === "Documents" && <DocumentsList />}
      {tab === "Activity" && <ShipmentAudit />}
      {tab !== "Overview" && tab !== "Documents" && tab !== "Activity" && (
        <Card title={tab}>
          <div style={{ padding: 30, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: 12.5 }}>{tab} view · {tab === "Contacts" ? "8 contacts" : tab === "Lanes" ? "24 active lanes" : tab === "Contracts" ? "3 master contracts, 12 spot agreements" : "AR balance $4,250 · YTD paid $284K"}</div>
        </Card>
      )}
    </div>
  );
}

/* ============================================================
   7. DRIVER DETAIL (CRM with compliance)
   ============================================================ */
export function DriverDetail() {
  const [tab, setTab] = useState("Overview");
  return (
    <div>
      <PageHeader kicker="DRIVER" title="Dwayne Johnson · M-204"
        subtitle="RY+ Gold · Kenworth T680 · 412K mi · 4.96 ★ · 99.6% OTD · $9,820 MTD"
        actions={<><Btn>Message</Btn><Btn>Assign Load</Btn><Btn primary>Edit Profile</Btn></>} />

      <TabBar tabs={["Overview", "Equipment", "Compliance", "Lanes", "Financial", "Bids", "Ratings"]} active={tab} onChange={setTab} />

      {tab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <Card title="Performance">
            <div style={{ display: "flex", gap: 8, padding: "8px 0", justifyContent: "space-around" }}>
              <Donut pct={99.6} color={GREEN} size={88} thickness={8} label="99.6%" />
              <Donut pct={98} color={ORANGE} size={88} thickness={8} label="4.96★" />
              <Donut pct={87} color={BLUE} size={88} thickness={8} label="87%" />
            </div>
            <div style={{ display: "flex", justifyContent: "space-around", fontSize: 10.5, color: "rgba(255,255,255,0.6)", textAlign: "center" }}>
              <span>OTD</span><span>Rating</span><span>Util</span>
            </div>
          </Card>
          <Card title="MTD Earnings">
            <div style={{ fontSize: 30, fontWeight: 800, color: "white", marginBottom: 4 }}>$9,820</div>
            <div style={{ fontSize: 11, color: GREEN, fontWeight: 700, marginBottom: 10 }}>↗ +18% vs last month</div>
            <MiniBars values={[1200, 1800, 1400, 2200, 1900, 2400, 2100, 2600, 2400, 2800]} color={ORANGE} height={50} />
            <Row k="Loads" v="14" />
            <Row k="Miles" v="9,140" />
            <Row k="Avg $/mi" v="$1.07" />
          </Card>
          <Card title="RY+ Gold Benefits">
            <div style={{ background: `linear-gradient(135deg, ${YELLOW}, #d97706)`, borderRadius: 11, padding: 14, marginBottom: 12, color: "white" }}>
              <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 800, opacity: 0.8 }}>RY+ GOLD MEMBER</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>$3,240 saved YTD</div>
              <div style={{ fontSize: 10.5, opacity: 0.8, marginTop: 2 }}>Member since Jan 2024 · next bill Jun 1</div>
            </div>
            <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
              ✓ Priority dispatch (1st pick)<br />
              ✓ 4% fuel rebate<br />
              ✓ Free factoring (instant pay)<br />
              ✓ Discounted insurance<br />
            </div>
          </Card>
        </div>
      )}

      {tab === "Compliance" && <ComplianceTab />}
      {tab !== "Overview" && tab !== "Compliance" && (
        <Card title={tab}><div style={{ padding: 30, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>{tab} details for Dwayne.</div></Card>
      )}
    </div>
  );
}
function ComplianceTab() {
  const docs = [
    { k: "MC Authority", v: "MC-948231", e: "Mar 14, 2027", days: 660, ok: true },
    { k: "DOT #", v: "DOT-3204891", e: "—", ok: true },
    { k: "Cargo Insurance ($100K)", v: "Progressive Commercial", e: "Aug 18, 2025", days: 87, ok: true },
    { k: "Liability ($1M)", v: "Progressive Commercial", e: "Aug 18, 2025", days: 87, ok: true },
    { k: "Drug Screen (DOT)", v: "Quest · clean", e: "Annual · Jul 5, 2025", days: 44, warn: true },
    { k: "Medical Card", v: "DOT Med · Dr. Patel", e: "Jun 12, 2025", days: 21, warn: true },
    { k: "IFTA Decals", v: "2025 Stickers issued", e: "Dec 31, 2025", days: 222, ok: true },
    { k: "W-9 / TIN", v: "On file · TD ACH", e: "—", ok: true },
  ];
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
        <KpiTile label="Compliance Score" value="92 / 100" delta="↗ Good" deltaColor={GREEN} icon={Icons.shield} />
        <KpiTile label="Active Docs" value={8} icon={Icons.doc} />
        <KpiTile label="Expiring ≤ 60d" value={2} deltaColor={YELLOW} delta="Action soon" icon={Icons.alert} />
        <KpiTile label="Last Audit" value="Apr 12" delta="Passed" deltaColor={GREEN} icon={Icons.check} />
      </div>
      <Card title="Compliance Documents">
        <Table columns={[
          { key: "k", label: "Document", bold: true },
          { key: "v", label: "Issuer / Reference" },
          { key: "e", label: "Expires" },
          { key: "days", label: "Days Left", align: "right", cell: (r) => r.days ? <span style={{ color: r.warn ? YELLOW : r.days < 30 ? RED : GREEN, fontWeight: 700 }}>{r.days}d</span> : <span style={{ color: "rgba(255,255,255,0.4)" }}>—</span> },
          { key: "status", label: "Status", cell: (r) => <Badge color={r.warn ? YELLOW : GREEN}>{r.warn ? "Renew soon" : "Active"}</Badge> },
          { key: "act", label: "", align: "right", cell: () => <Btn size="sm">View</Btn> },
        ]} rows={docs} />
      </Card>
    </>
  );
}

/* ============================================================
   8. COMMUNICATIONS CENTRE
   ============================================================ */
export function Communications() {
  const [selected, setSelected] = useState(0);
  const threads = [
    { who: "Dwayne Johnson · ABC Mfg.", ch: "Email", last: "On track for 6:00 PM. POD will be auto-sent…", t: "10m", unread: 0, c: BLUE },
    { who: "D. Johnson · RY+ Gold", ch: "SMS", last: "32 mi out, on track for 6:00 PM.", t: "12m", unread: 0, c: GREEN },
    { who: "Sunrise Distributors", ch: "Email", last: "Quote QTE-2050 declined. Going with…", t: "1h", unread: 2, c: BLUE },
    { who: "Driver Channel #dispatch", ch: "Internal", last: "Dwayne: Anyone within 100mi of Memphis?", t: "2h", unread: 5, c: PURPLE },
    { who: "Global Tech", ch: "Email", last: "Need RFP response by Friday. 24 lanes…", t: "3h", unread: 1, c: BLUE },
    { who: "D. Johnson · RY+ Silver", ch: "SMS", last: "HOS running out, need yard near Birmingham", t: "4h", unread: 1, c: GREEN },
    { who: "ABC Mfg. Accounting", ch: "Email", last: "Wire sent for INV-2025-000158, ref…", t: "Yesterday", unread: 0, c: BLUE },
    { who: "Safety @ DOT", ch: "Email", last: "Roadside inspection report for T-307…", t: "Yesterday", unread: 1, c: RED },
  ];
  return (
    <div>
      <PageHeader kicker="UNIFIED INBOX" title="Communications Centre" subtitle="248 threads · 12 unread · email + SMS + driver chat"
        actions={<><Btn>Templates</Btn><Btn primary>Compose</Btn></>} />
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 14, height: 600 }}>
        <Card pad={10}>
          <ChipRow chips={["All · 248", "Unread · 12", "Email", "SMS", "Internal"]} />
          <div style={{ overflow: "auto", maxHeight: 500 }}>
            {threads.map((t, i) => (
              <div key={i} onClick={() => setSelected(i)} className="ry-clickable"
                style={{ padding: 10, borderRadius: 8, background: selected === i ? `${ORANGE}1a` : "transparent", border: `0.5px solid ${selected === i ? ORANGE : "transparent"}`, marginBottom: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "white" }}>{t.who}</div>
                  <span style={{ fontSize: 9.5, color: "rgba(255,255,255,0.45)" }}>{t.t}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{t.last}</div>
                  {t.unread > 0 && <span style={{ background: ORANGE, color: "white", fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 999, flexShrink: 0 }}>{t.unread}</span>}
                </div>
                <Badge color={t.c} size={9}>{t.ch}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, paddingBottom: 12, borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "white" }}>{threads[selected].who}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{threads[selected].ch} · Re: Shipment RY-78432</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn size="sm">Link to Shipment</Btn>
              <Btn size="sm">Assign</Btn>
              <Btn size="sm">{Icons.more}</Btn>
            </div>
          </div>
          <div style={{ flex: 1, overflow: "auto", marginBottom: 12 }}>
            {[
              { from: "them", t: "May 17 · 9:08 AM", body: "Can you book this Toronto → Chicago, 28 pallets, May 19 pickup?" },
              { from: "you", t: "May 17 · 9:14 AM", body: "Yes — AI quote came back at $4,250 all-in. Within your spot budget. Booking now and assigning D. Johnson (RY+ Gold, 4.96★, 78 prior loads on this lane)." },
              { from: "them", t: "May 17 · 10:30 AM", body: "Approved. Please POD to receiving@abcmfg.com on signature." },
              { from: "you", t: "May 19 · 9:18 AM", body: "Picked up — BOL signed, photos in portal. ETA Chicago 6:00 PM Tuesday." },
              { from: "them", t: "Today · 5:30 PM", body: "Hi Dwayne — any update? Customer is asking." },
              { from: "you", t: "Today · 5:32 PM", body: "On track for 6:00 PM. POD will be auto-sent on signature." },
            ].map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.from === "you" ? "flex-end" : "flex-start", marginBottom: 10 }}>
                <div style={{ maxWidth: "70%", background: m.from === "you" ? ORANGE : "rgba(255,255,255,0.06)", color: "white", padding: "8px 12px", borderRadius: 11, fontSize: 12, lineHeight: 1.45 }}>
                  {m.body}
                  <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 4, textAlign: m.from === "you" ? "right" : "left" }}>{m.t}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Type reply, ⌘+enter to send…</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 6, color: "rgba(255,255,255,0.5)" }}>{Icons.upload}{Icons.doc}{Icons.msg}</div>
              <Btn primary size="sm">Send {Icons.send}</Btn>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
